import type { TaskListTypeFilter } from '@/features/tasks/api/watchTaskApi'
import { tasksListPath, type TasksListState } from '@/lib/tasks/tasksTabs'

import {
  TASK_FEED_PLATFORM_ICON,
  type TaskFeedPlatform
} from '../TaskFeedCard/taskFeedCard.constants'
import {
  EXPLORE_FEED_SORTS,
  MY_FEED_TYPE_OPTIONS,
  type TasksFeedSortId
} from '../TasksFeedNav/tasksFeedNav.constants'
import {
  TASKS_FILTER_SEGMENTS,
  type TasksFilterSegmentId
} from '../TasksFilterBar/tasksFilterBar.constants'

/** The list filters kept in the URL, so Back from a task restores them. */
export type TasksListFilters = {
  segment: TasksFilterSegmentId
  platforms: readonly TaskFeedPlatform[]
  types: readonly TaskListTypeFilter[]
  sort: TasksFeedSortId
}

const NONE: readonly never[] = []

const PLATFORMS = Object.keys(TASK_FEED_PLATFORM_ICON) as TaskFeedPlatform[]

function known<T extends string>(
  values: string[],
  allowed: readonly T[]
): readonly T[] {
  const list = values.filter((v): v is T => allowed.includes(v as T))
  return list.length > 0 ? list : NONE
}

// `type` is the type chip on Explore and the Task type menu on My tasks.
export function parseTasksListFilters(
  params: URLSearchParams
): TasksListFilters {
  const type = params.get('type')
  const sort = params.get('sort')
  return {
    segment: TASKS_FILTER_SEGMENTS.find(id => id === type) ?? 'all',
    platforms: known(params.getAll('platform'), PLATFORMS),
    types: known(params.getAll('type'), MY_FEED_TYPE_OPTIONS),
    sort: EXPLORE_FEED_SORTS.find(id => id === sort) ?? 'default'
  }
}

export function tasksListUrl(
  state: TasksListState,
  filters: TasksListFilters
): string {
  const params = new URLSearchParams()
  if (state.view === 'explore') {
    if (filters.segment !== 'all') {
      params.append('type', filters.segment)
    }
  } else {
    for (const type of filters.types) {
      params.append('type', type)
    }
  }
  for (const platform of filters.platforms) {
    params.append('platform', platform)
  }
  if (filters.sort !== 'default') {
    params.set('sort', filters.sort)
  }

  const path = tasksListPath(state)
  const query = params.toString()
  if (!query) {
    return path
  }
  return `${path}${path.includes('?') ? '&' : '?'}${query}`
}
